import resolveFrontendModule from "@app/resolveModule/frontend";
import CaptionToggleButton from "@components/controls/CaptionToggleButton";
import DiagramType from "@core/components/Diagram/DiagramType";
import Path from "@core/FileProvider/Path/Path";
import FetchService from "@core-ui/ApiServices/FetchService";
import ApiUrlCreatorService from "@core-ui/ContextServices/ApiUrlCreator";
import IsMacService from "@core-ui/ContextServices/IsMac";
import PageDataContextService from "@core-ui/ContextServices/PageDataContext";
import WorkspaceService from "@core-ui/ContextServices/Workspace";
import downloadResource from "@core-ui/downloadResource";
import { usePlatform } from "@core-ui/hooks/usePlatform";
import toggleSignature from "@core-ui/toggleSignature";
import t from "@ext/localization/locale/translate";
import type { Editor } from "@tiptap/core";
import type { Node } from "@tiptap/pm/model";
import { GlassToolbarButton, GlassToolbarIcon } from "@ui-kit/GlassToolbar";
import type { Dispatch, RefObject, SetStateAction } from "react";

interface DiagramActionsProps {
	hasSignature: boolean;
	editor: Editor;
	node: Node;
	signatureRef: RefObject<HTMLInputElement>;
	openEditor: () => void;
	setHasSignature: Dispatch<SetStateAction<boolean>>;
}

const DiagramActions = ({
	editor,
	node,
	hasSignature,
	setHasSignature,
	signatureRef,
	openEditor,
}: DiagramActionsProps) => {
	const updateAttributes = (attributes: Record<string, string>) => {
		editor.commands.updateAttributes(node.type, attributes);
	};
	const diagramName = node.attrs.diagramName;
	const apiUrlCreator = ApiUrlCreatorService.value;
	const workspace = WorkspaceService.current();
	const { isTauri } = usePlatform();
	const isMac = IsMacService.value;
	const disabledEdit =
		!PageDataContextService.value.settings?.services?.["diagram-renderer"]?.endpoint &&
		diagramName === DiagramType["plant-uml"];
	const editTooltip = disabledEdit ? t("diagram.error.no-diagram-renderer") : t("edit2");
	const signatureTooltip = t("signature");
	const downloadTooltip = t("download");
	const explorerTooltip = isMac ? t("open-in.finder") : t("open-in.explorer");

	const src: string | undefined = node.attrs.src;

	const addSignature = () => {
		setHasSignature((prev) => toggleSignature(prev, signatureRef.current, updateAttributes));
	};

	const download = () => downloadResource(apiUrlCreator, new Path(src));

	const showInExplorer = async () => {
		const response = await FetchService.fetch(apiUrlCreator.getResourcePath(src));
		if (!response.ok) return;
		const absolutePath = await response.text();
		await resolveFrontendModule("openInExplorer")?.(new Path(workspace.path).join(new Path(absolutePath)).value);
	};

	return (
		<>
			<GlassToolbarButton
				aria-label={editTooltip}
				className="disabled:pointer-events-auto disabled:cursor-not-allowed"
				data-testid="edit-diagram"
				disabled={disabledEdit}
				onClick={openEditor}
				tooltipText={editTooltip}
			>
				<GlassToolbarIcon icon="pencil" />
			</GlassToolbarButton>
			<CaptionToggleButton
				active={hasSignature}
				caption={node.attrs.title}
				onToggle={addSignature}
				tooltipText={signatureTooltip}
			/>
			{src && (
				<GlassToolbarButton
					aria-label={downloadTooltip}
					data-testid="download-diagram"
					onClick={download}
					tooltipText={downloadTooltip}
				>
					<GlassToolbarIcon icon="download" />
				</GlassToolbarButton>
			)}
			{src && isTauri && workspace && (
				<GlassToolbarButton
					aria-label={explorerTooltip}
					data-testid="show-diagram-in-explorer"
					onClick={showInExplorer}
					tooltipText={explorerTooltip}
				>
					<GlassToolbarIcon icon="folder-open" />
				</GlassToolbarButton>
			)}
		</>
	);
};

export default DiagramActions;
